import React from "react";
import { Lock, Clock } from "lucide-react";

interface ClientLockIndicatorProps {
  clientId: string;
  isLocked: boolean;
  isLockedByMe: boolean;
  lockInfo: {
    userName: string;
    lockedAt: string;
  } | null;
  className?: string;
}

const ClientLockIndicator: React.FC<ClientLockIndicatorProps> = ({
  clientId,
  isLocked,
  isLockedByMe,
  lockInfo,
  className = "",
}) => {
  if (!isLocked) {
    return null;
  }

  const formatTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const lockedAt = new Date(timestamp);
    const diffInMinutes = Math.floor(
      (now.getTime() - lockedAt.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  if (isLockedByMe) {
    return (
      <div
        className={`flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded-md ${className}`}
      >
        <Lock className="h-4 w-4 text-green-600" />
        <div className="text-sm">
          <div className="font-medium text-green-700">You are editing</div>
          <div className="text-xs text-green-600">
            {formatTimeAgo(lockInfo?.lockedAt || "")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 px-2 py-1 bg-red-50 border border-red-200 rounded-md ${className}`}
    >
      <Lock className="h-4 w-4 text-red-600" />
      <div className="text-xs">
        <div className="font-medium text-red-700">
          Editing: {lockInfo?.userName || "another user"}
        </div>
        <div className="flex items-center gap-1 text-xs text-red-600">
          <Clock className="h-3 w-3" />
          {formatTimeAgo(lockInfo?.lockedAt || "")}
        </div>
      </div>
    </div>
  );
};

export default ClientLockIndicator;
